import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { useTagSelector } from '../../../kanban/components/lead-detail/hooks/useTagSelector';
import TagFormModal from '../../../settings/tags/components/TagFormModal';
import ConfirmationModal from '../../../../components/shared/ConfirmationModal';
import { TagDropdown } from './TagDropdown';

const TAG_BADGES = [
    'badge-success',
    'badge-warning',
    'badge-info',
    'badge-brand',
    'badge-brand-dark',
    'badge-danger',
] as const;

const tagColorById = new Map<string, (typeof TAG_BADGES)[number]>();

const tagBadgeClass = (id: string) => {
    const cached = tagColorById.get(id);
    if (cached) return cached;
    const color = TAG_BADGES[tagColorById.size % TAG_BADGES.length];
    tagColorById.set(id, color);
    return color;
};

interface ChatTagSelectorProps {
    selectedTagIds: string[];
    onTagsChange: (tagIds: string[]) => void;
}

export const ChatTagSelector: React.FC<ChatTagSelectorProps> = ({
    selectedTagIds,
    onTagsChange
}) => {
    const {
        isOpen,
        setIsOpen,
        searchTerm,
        setSearchTerm,
        isCreateModalOpen,
        setIsCreateModalOpen,
        dropdownRef,
        selectedTagObjects,
        filteredTags,
        handleAddTag,
        handleRemoveTag,
        handleCreateTag,
        handleUpdateTag,
        handleDeleteTagSystem,
        tagToEdit,
        openEditModal,
        openDeleteModal,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        tagToDelete
    } = useTagSelector({ selectedTags: selectedTagIds, onTagsChange });

    return (
        <>
            <div className="relative">
                <ul className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0">
                    {selectedTagObjects.map(tag => (
                        <li key={tag.id}>
                            <span
                                className={cn('badge badge-sm max-w-[8rem]', tagBadgeClass(tag.id))}
                                title={tag.name}
                            >
                                <span className="truncate">{tag.name}</span>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveTag(tag.id)}
                                    className="chip-remove shrink-0"
                                    title="Remover tag"
                                    aria-label={`Remover tag ${tag.name}`}
                                >
                                    <AppIcon name="x" className="h-3 w-3" />
                                </button>
                            </span>
                        </li>
                    ))}
                    <li>
                        <button
                            type="button"
                            onClick={() => setIsOpen(!isOpen)}
                            className="chip chip--dashed min-h-11 touch-manipulation md:min-h-7"
                            aria-expanded={isOpen}
                            aria-haspopup="listbox"
                        >
                            <AppIcon name="tag" className="h-3.5 w-3.5" aria-hidden="true" />
                            <span>Adicionar tag</span>
                        </button>
                    </li>
                </ul>

                <TagDropdown
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    dropdownRef={dropdownRef}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    filteredTags={filteredTags}
                    onSelectTag={handleAddTag}
                    onEditTag={openEditModal}
                    onDeleteTag={openDeleteModal}
                    onCreateNew={() => {
                        setIsOpen(false);
                        setIsCreateModalOpen(true);
                    }}
                />
            </div>

            <TagFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSave={tagToEdit ? handleUpdateTag : handleCreateTag}
                tagToEdit={tagToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteTagSystem}
                title="Excluir Etiqueta"
                message={`Tem certeza que deseja excluir a etiqueta "${tagToDelete?.name}"? Isso a removerá de todos os contatos e negociações.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />
        </>
    );
};
